// ABOUTME: SPEC-53 — the product card of a static collection piece (Figma 403:1265 / container
// ABOUTME: 403:1448), over the carousel; desktop split 1333/1333, mobile in the Vesper card's band.
//
// Data is the normalised envelope that rides inside `stage_command.open_piece.product` (review R8:
// never a `view: "PRODUCT"` ui_update, or the hidden grid card would mount). The card owns nothing
// but presentation: the gallery index lives in the stage machine so `switch_gallery_image` /
// `gallery_navigate` and the arrows move the same number; heart and bag are the App's own
// wishlist/cart handlers (the same "added to wishlist" / "added to cart" interactions the reel
// panels send, so the backend's MANUAL_SELECTION_ACTIONS sync silently); the body scrolls by the
// rail panels' chevron path and reports as panel "PRODUCT".
//
// Left button, "Back to <reel>", returns to the FILM (owner ruling 6). Owner ruling 6 also called
// closing the card back to its carousel voice-only; client override (2026-09-11) adds an explicit
// X for that instead — it drives the exact same `close_piece` transition the backend already used
// for the voice path (stageReducer.js: PIECE -> COLLECTION, never all the way to the reel), so
// there is still only one way this card closes, just two ways to ask for it. The design's other
// right-hand buttons, collapsible sections, "Virtual Try On" and the "C" tile remain deliberately
// absent (owner brief, §8 non-goals) — this override is scoped to the close affordance only.

import React from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { RAIL_UNIT, PANEL_RADIUS, u, uMin } from '../ReelChrome/railGeometry';
import { MOBILE_UNIT, mu, muMin, safeTop, ICON_RAIL_MAX_BOTTOM, RAIL_TOUCH_OVERSHOOT_GUARD } from '../ReelChrome/mobileGeometry';
import { useViewportSize } from '../Projectionist/useViewportSize';
import { PieceGallery, PieceDetails } from './pieceCardParts';
import { imagesOf } from './pieceEnvelope';
import { PIECE_CARD_LAYOUT, mobileCardHeight, pieceCardLayoutFor } from './pieceCardLayout';
import { useControlsLift, useLiftTransition } from '../../uiExperiment/bottomChromeStore'; // SPEC-82 §4.7

/** Container `403:1448`: 2666x1333 at (587, 414) in the 3840x2160 frame, as viewport percentages. */
export const CARD_BOX = {
  leftPct: (587 / 3840) * 100,      // 15.29
  topPct: (414 / 2160) * 100,       // 19.17
  widthPct: (2666 / 3840) * 100,    // 69.43
  heightPct: (1333 / 2160) * 100,   // 61.71
  pad: 53.333,
};
/** Left button `403:1486` at (156, 947). Its label is the reel's name, never authored here. */
export const BACK_BUTTON = { leftPct: (156 / 3840) * 100, topPct: (947 / 2160) * 100, h: 68.667, padX: 40, radius: 40, font: 26.667 };
// D-7: the gutter between the back button's own left edge and the card's, less one authored gap —
// a long "Back to <reel>" label truncates inside it instead of painting over the card (2026-09-16
// report). Derived from the two leftPcts rather than written out so a retune of either box's
// position keeps the cap in step automatically.
const BACK_BUTTON_GUTTER_PCT = CARD_BOX.leftPct - BACK_BUTTON.leftPct; // 11.223

/**
 * Mobile (owner ruling 8: no design; reuse the Vesper mobile card's composition and concept). The
 * Vesper's mobile card and sheet are a frosted band spanning the footer's own width, anchored
 * above the CC bar (SpecSheetOverlay MOBILE: w 1142, bottom 313, pad 45, radius 53.333). This card
 * takes that band and grows it upward to below the icon rail, image on top, text below.
 *
 * `top` used to be a flat 300, which cleared the wordmark and a single collapsed chip fine but
 * collided with the Ophion `alwaysExpanded` rail's three permanently-stacked chips (mobileGeometry's
 * ICON_RAIL_MAX_BOTTOM, ~396.67) — reported as the card sitting "too high" and covering the top-right
 * icons (2026-09-15). Deriving `top` from the rail's own worst-case extent instead of a copied number
 * fixes that on every tenant and, as a side effect, shrinks the band toward the CC bar the way the
 * fix was also asked to.
 *
 * The gap past that extent is `RAIL_TOUCH_OVERSHOOT_GUARD`, not `MENU_GAP` — a first pass used
 * `MENU_GAP` (13.333, a visual rhythm value) and the close X still lost taps on a real phone: the
 * rail's chips are `.reel-touch-target`, a 44 REAL px minimum hit box invisibly bigger than the
 * chip itself below ~608px viewports, and that overshoot ate the thin gap. See
 * RAIL_TOUCH_OVERSHOOT_GUARD's own comment for the arithmetic.
 */
// `imageShare` (SPEC-62 D-5): '42%' undersized the product image to about the same size as its own
// thumbnails on a short viewport (2026-09-16 CPO report); sourced from the one tunable rather than a
// literal so `pieceCardLayout.js` stays the only place that number lives. `bottom: 313` stays as the
// authored CC-bar gutter — no longer an inline style (see `mobileCardHeight()`, D-1), but the same
// number `pieceCardLayoutFor` uses to decide the floored height and the height CSS composes from.
export const MOBILE_CARD = {
  w: 1142, top: ICON_RAIL_MAX_BOTTOM + RAIL_TOUCH_OVERSHOOT_GUARD, bottom: 313, pad: 45,
  radius: 53.333, imageShare: `${PIECE_CARD_LAYOUT.mobileImageShare * 100}%`,
};

const shell = 'bg-reel-chrome-panel backdrop-blur-reel-chrome text-reel-chrome-textPrimary';

// D-6: below ~868px of viewport width the two-column grid cannot hold its own contents (a phone with
// "Request Desktop Site" is the reachable case — SPEC-62 §1.2(b)), so the stacked branch swaps the
// absolutely-positioned back button for this inline row through `PieceDetails`'s existing `leading`
// prop — the same seam `MobileCard` already uses below, not a third composition.
function DesktopBackRow({ backLabel, onBackToReel }) {
  return (
    <div className="flex items-start" style={{ marginBottom: u(20) }}>
      <button
        type="button"
        data-testid="piece-back"
        onClick={onBackToReel}
        className="self-start flex items-center font-reel-body text-reel-chrome-textDim hover:text-reel-chrome-textPrimary bg-transparent border-0 p-0"
        style={{ fontSize: uMin(BACK_BUTTON.font, 12), gap: u(8) }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
        {backLabel}
      </button>
    </div>
  );
}

function DesktopCard(props) {
  const { backLabel, onBackToReel, onClose, layout } = props;
  const { stacked, gridTemplateColumns, showThumbs } = layout;
  return (
    <div data-testid="piece-card" data-composition="desktop" data-stacked={String(stacked)} data-thumbs={showThumbs ? 'on' : 'off'} className="absolute inset-0 pointer-events-none" style={{ '--u': RAIL_UNIT }}>
      <div
        className={`absolute pointer-events-auto ${shell}`}
        style={{
          left: `${CARD_BOX.leftPct}%`, top: `${CARD_BOX.topPct}%`,
          width: `${CARD_BOX.widthPct}%`, height: `${CARD_BOX.heightPct}%`,
          borderRadius: u(PANEL_RADIUS),
          display: 'grid', gridTemplateColumns,
          // D-5/D-6: stacked rows split image-over-details at the same 58/42 the mobile card uses
          // (PIECE_CARD_LAYOUT.mobileImageShare) — one number, both compositions' "image is the
          // subject" split.
          ...(stacked ? { gridTemplateRows: `${PIECE_CARD_LAYOUT.mobileImageShare * 100}% ${(1 - PIECE_CARD_LAYOUT.mobileImageShare) * 100}%` } : {}),
          overflow: 'hidden', contain: 'paint',
        }}
      >
        {/* `min-w-0` on both grid children: a CSS Grid item's default automatic minimum width is
            its own min-content size, not the track's actual `1fr` share — invisible with two roomy
            columns, but the stacked branch's single `1fr` track discovered it (2026-09-16 plate
            review): the details column's header row (title + heart/bag) forced the track ~55px
            wider than the card's own box, and the overflow sat behind the card's `overflow: hidden`
            with the bag icon entirely clipped out of frame. Same fix as the flex `min-w-0` on
            PieceDetails's own title (pieceCardParts.js) — one level up. */}
        <div className="min-h-0 min-w-0" style={{ padding: u(CARD_BOX.pad), borderRight: stacked ? 'none' : '1px solid rgba(243,240,233,0.15)' }}>
          <PieceGallery images={props.images} index={props.index} onIndexChange={props.onGalleryIndexChange} title={props.product.title} lens thumbs={showThumbs} />
        </div>
        <div className="min-h-0 min-w-0 flex flex-col" style={{ padding: u(CARD_BOX.pad) }}>
          <PieceDetails {...props} leading={stacked ? <DesktopBackRow backLabel={backLabel} onBackToReel={onBackToReel} /> : null} />
        </div>
      </div>
      {/* Floating close, slightly outside the card's own top-right corner — has to live outside
          the card's `overflow: hidden` box above, or the rounded corner clips it. */}
      {onClose && (
        <button
          type="button"
          data-testid="piece-close"
          onClick={onClose}
          aria-label="Close product details"
          className={`absolute pointer-events-auto flex items-center justify-center rounded-full hover:bg-white/15 transition-colors ${shell}`}
          style={{
            left: `calc(${CARD_BOX.leftPct + CARD_BOX.widthPct}% - ${u(56)})`,
            top: `calc(${CARD_BOX.topPct}% - ${u(28)})`,
            width: uMin(64, 32), height: uMin(64, 32),
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
      {/* D-6: the absolutely-positioned button only exists in the wide, two-column layout — stacked
          renders the inline row above instead, never both, so `piece-back` is never duplicated. */}
      {!stacked && (
        <button
          type="button"
          data-testid="piece-back"
          onClick={onBackToReel}
          className={`absolute pointer-events-auto flex items-center justify-center font-reel-body ${shell} hover:bg-white/15 transition-colors`}
          style={{
            left: `${BACK_BUTTON.leftPct}%`, top: `${BACK_BUTTON.topPct}%`,
            height: uMin(BACK_BUTTON.h, 36), padding: `0 ${u(BACK_BUTTON.padX)}`,
            borderRadius: u(BACK_BUTTON.radius), fontSize: uMin(BACK_BUTTON.font, 12),
            // D-7: a long "Back to <reel>" label truncates at the gutter instead of crossing into
            // the card (2026-09-16 report) — pure CSS, no reposition, so nothing that fits today moves.
            maxWidth: `calc(${BACK_BUTTON_GUTTER_PCT}% - ${u(PIECE_CARD_LAYOUT.backButtonGutterGap)})`,
          }}
        >
          {/* `min-width: 0` is what lets a flex child actually shrink below its content size —
              without it the ellipsis never triggers, the button's own `maxWidth` just gets ignored
              and the label pushes the box wide again. */}
          <span className="overflow-hidden whitespace-nowrap" style={{ textOverflow: 'ellipsis', minWidth: 0 }}>
            {backLabel}
          </span>
        </button>
      )}
    </div>
  );
}

function MobileCard(props) {
  const { backLabel, onBackToReel, onClose } = props;
  const back = (
    <div className="flex items-start" style={{ marginBottom: mu(20) }}>
      <button
        type="button"
        data-testid="piece-back"
        onClick={onBackToReel}
        className="self-start flex items-center font-reel-body text-reel-chrome-textDim hover:text-reel-chrome-textPrimary bg-transparent border-0 p-0"
        style={{ fontSize: uMin(37.333, 12), gap: mu(8) }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
        {backLabel}
      </button>
    </div>
  );
  const { layout } = props;
  return (
    <div data-testid="piece-card" data-composition="mobile" data-stacked={String(!!layout.stacked)} data-thumbs={layout.showThumbs ? 'on' : 'off'} className="absolute inset-0 pointer-events-none" style={{ '--u': MOBILE_UNIT }}>
      <div
        className={`absolute pointer-events-auto flex flex-col ${shell}`}
        style={{
          left: '50%', transform: 'translateX(-50%) translateZ(0)',
          width: mu(MOBILE_CARD.w), maxWidth: '94vw',
          // D-1/D-2: `top` never yields (it's a tap-loss constraint — the rail's overshoot guard);
          // the CC-bar gutter does. `height` replaces the old fixed `bottom` offset so the card
          // floors instead of starving/erasing on a short viewport (SPEC-62 §1.1).
          top: safeTop(MOBILE_CARD.top), height: mobileCardHeight(props.lift), transition: props.liftTransition,
          padding: mu(MOBILE_CARD.pad), borderRadius: mu(MOBILE_CARD.radius), overflow: 'hidden', contain: 'paint',
        }}
      >
        {/* Floating close, top-right of the card's own image — the mobile twin of the desktop
            card's floating X (client override of the "voice only" ruling recorded in the file
            banner). Used to sit inline in the back row instead (2026-09-11); moved here
            (2026-09-15) once the Ophion `alwaysExpanded` icon rail made that row's corner the
            expected place to look for it, matching desktop. Kept INSIDE the card's own padding
            (unlike desktop's bleed-outside-the-corner treatment) because the card's top edge
            already sits right under that rail (mobileGeometry's ICON_RAIL_MAX_BOTTOM) — bleeding
            upward the way desktop does would put it right back under the rail it was moved to
            clear. */}
        {onClose && (
          <button
            type="button"
            data-testid="piece-close"
            onClick={onClose}
            aria-label="Close product details"
            className="absolute pointer-events-auto flex items-center justify-center rounded-full hover:bg-white/15 transition-colors"
            style={{
              top: mu(MOBILE_CARD.pad), right: mu(MOBILE_CARD.pad),
              width: muMin(64, 32), height: muMin(64, 32),
              background: 'rgba(67,67,67,0.21)',
              // Explicit, above PieceGallery's own arrow buttons (pieceCardParts.js's z-50) — the
              // shell's `contain: paint` scopes both to the same local stacking context, and
              // without this the close button (implicit z-index:0) could lose a hit-test race to
              // them if a future gallery-height tweak ever let the two boxes overlap.
              zIndex: 60,
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
        <div className="min-h-0" style={{ flex: `0 0 ${MOBILE_CARD.imageShare}` }}>
          <PieceGallery images={props.images} index={props.index} onIndexChange={props.onGalleryIndexChange} title={props.product.title} lens={false} thumbs={layout.showThumbs} />
        </div>
        <div className="min-h-0 flex-1 flex flex-col" style={{ marginTop: mu(28) }}>
          <PieceDetails {...props} leading={back} />
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   product: object, collectionTitle?: string, backLabel: string,
 *   wishlisted: boolean, inBag: boolean,
 *   onToggleWishlist: (p: object) => void, onAddToBag: (p: object) => void, onBackToReel: () => void,
 *   onClose?: () => void,  // the explicit X (client override) — PIECE -> COLLECTION, not the reel
 *   galleryIndex: number, onGalleryIndexChange: (i: number) => void,
 *   scrollCommand?: {panel: string, direction: string, nonce: number}|null,
 *   onScrollStateChange?: (state: object) => void,
 *   composition?: 'desktop'|'mobile',
 * }} props
 */
export default function CollectionPieceCard(props) {
  const { product, composition = 'desktop', galleryIndex = 0 } = props;
  // Read unconditionally, ahead of the `!product` early return, so the hook order never changes
  // across renders (SPEC-62 S7) — IconRail.js:557 is the precedent for a chrome component reading
  // the unit in JS this way.
  const viewport = useViewportSize();
  // SPEC-82 control_reveal: the revealed row above the orb sat over the card's bottom; the card yields the row's
  // lift from its bottom edge (its top is the rail's tap-loss guard and never moves). 0 while the row is closed.
  const lift = useControlsLift('mobile');
  const liftTransition = useLiftTransition('height');
  if (!product) return null;
  const images = imagesOf(product);
  const index = images.length ? Math.min(Math.max(0, galleryIndex | 0), images.length - 1) : 0;
  const mobileLift = composition === 'mobile' ? lift : 0;
  const layout = pieceCardLayoutFor(viewport, composition, mobileLift);
  const shared = { ...props, images, index, layout, lift: mobileLift, liftTransition };
  return composition === 'mobile' ? <MobileCard {...shared} /> : <DesktopCard {...shared} />;
}
